<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Evaluación al Desempeño Docente</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <!-- Contexto preseleccionado (viene desde Horario / Boleta)-->
          <div v-if="preloaded" class="mb-4 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded flex items-center gap-2 text-sm">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span>
              Evaluando: <strong>{{ ctx.nombre_materia }}</strong> —
              Docente: <strong>{{ ctx.nombre_docente }}</strong> —
              Periodo: <strong>{{ ctx.id_periodo }}</strong>
            </span>
          </div>

          <!-- Selector manual (viene desde menú)-->
          <div v-if="!preloaded && fase === 'selector'" class="bg-white shadow rounded-lg p-6 mb-6">
            <h2 class="text-lg font-semibold mb-4">Selecciona el periodo y docente a evaluar</h2>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Periodo académico</label>
              <select v-model="ctx.id_periodo" @change="onPeriodChange"
                class="block w-full sm:w-72 border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="">-- Selecciona un periodo --</option>
                <option v-for="p in periodos" :key="p.idPeriod" :value="p.idPeriod">{{ p.idPeriod }}</option>
              </select>
            </div>

            <div v-if="ctx.id_periodo" class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-1">Materia / Docente</label>
              <select v-model="scheduleKey" @change="onScheduleKeyChange"
                class="block w-full sm:w-96 border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500">
                <option value="">-- Selecciona materia y docente --</option>
                <option v-for="item in subjectsForPeriod" :key="item.key" :value="item.key">
                  {{ item.subject }} — {{ item.teacher }}
                </option>
              </select>
            </div>

            <button
              :disabled="!scheduleKey || cargandoFormulario"
              @click="iniciarEvaluacion"
              class="inline-flex items-center px-4 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              <svg v-if="cargandoFormulario" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              {{ cargandoFormulario ? 'Cargando...' : 'Iniciar evaluación' }}
            </button>
          </div>

          <!-- Mensajes de estado-->
          <div v-if="fase === 'ya_evaluado'" class="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"/>
            </svg>
            Ya realizaste la evaluación de este docente para el periodo seleccionado.
          </div>

          <div v-if="fase === 'ventana_cerrada'" class="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <svg class="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            El periodo de evaluación docente no está activo en este momento.
          </div>

          <div v-if="errorCarga" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
            {{ errorCarga }}
          </div>

          <!-- FORMULARIO DINÁMICO-->
          <div v-if="fase === 'formulario' && formulario" class="bg-white shadow rounded-lg p-6">
            <h2 class="text-lg font-semibold mb-1">{{ formulario.nombre }}</h2>
            <p class="text-sm text-gray-500 mb-6">
              Todas las preguntas marcadas con <span class="text-red-500">*</span> son obligatorias.
            </p>

            <!-- Itera las preguntas dinámicamente -->
            <div v-for="(pregunta, idx) in formulario.preguntas" :key="pregunta.id" class="mb-7">

              <!-- Texto de la pregunta -->
              <label class="block text-sm font-medium text-gray-700 mb-2">
                {{ idx + 1 }}. {{ pregunta.texto }}
                <span v-if="pregunta.es_requerida" class="text-red-500">*</span>
                <span v-if="pregunta.ayuda" class="text-xs text-gray-400 font-normal ml-1">({{ pregunta.ayuda }})</span>
              </label>

              <!-- RADIO-->
              <div v-if="pregunta.tipo === 'radio'" class="flex flex-wrap gap-2">
                <label
                  v-for="op in pregunta.opciones" :key="op.valor"
                  class="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors"
                  :class="respuestas[pregunta.clave] === op.valor
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300'"
                >
                  <input type="radio" v-model="respuestas[pregunta.clave]" :value="op.valor" class="hidden"/>
                  {{ op.etiqueta }}
                </label>
              </div>

              <!-- CHECKBOX-->
              <div v-else-if="pregunta.tipo === 'checkbox'" class="flex flex-wrap gap-2">
                <label
                  v-for="op in pregunta.opciones" :key="op.valor"
                  class="flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors"
                  :class="(respuestas[pregunta.clave] || []).includes(op.valor)
                    ? 'bg-blue-50 border-blue-500 text-blue-700'
                    : 'border-gray-200 hover:border-blue-300'"
                >
                  <input type="checkbox"
                    :checked="(respuestas[pregunta.clave] || []).includes(op.valor)"
                    @change="toggleCheckbox(pregunta.clave, op.valor)"
                    class="hidden"/>
                  {{ op.etiqueta }}
                </label>
              </div>

              <!-- ESCALA-->
              <div v-else-if="pregunta.tipo === 'escala'" class="flex gap-3">
                <label
                  v-for="n in escalaRango(pregunta.escala_min, pregunta.escala_max)" :key="n"
                  class="w-10 h-10 flex items-center justify-center rounded-full border-2 cursor-pointer font-bold text-sm transition-colors"
                  :class="respuestas[pregunta.clave] === String(n)
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-300 hover:border-blue-400 text-gray-600'"
                >
                  <input type="radio" v-model="respuestas[pregunta.clave]" :value="String(n)" class="hidden"/>
                  {{ n }}
                </label>
              </div>

              <!-- TEXTAREA-->
              <div v-else-if="pregunta.tipo === 'textarea'">
                <textarea
                  v-model="respuestas[pregunta.clave]"
                  rows="3"
                  placeholder="Escribe tu respuesta aquí..."
                  class="block w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                ></textarea>
              </div>

              <!-- LIKERT GRUPO-->
              <div v-else-if="pregunta.tipo === 'likert_grupo'" class="overflow-x-auto">
                <table class="min-w-full text-sm">
                  <thead>
                    <tr class="bg-gray-50">
                      <th class="text-left px-3 py-2 font-medium text-gray-600 w-48 text-xs">Aspecto</th>
                      <th
                        v-for="n in escalaRango(pregunta.escala_min, pregunta.escala_max)" :key="n"
                        class="px-2 py-2 text-center font-medium text-gray-600 text-xs min-w-[44px]"
                      >{{ n }}</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100">
                    <tr v-for="sub in pregunta.subpreguntas" :key="sub.id" class="hover:bg-gray-50">
                      <td class="px-3 py-3 text-gray-700 text-xs">{{ sub.texto }}</td>
                      <td
                        v-for="n in escalaRango(pregunta.escala_min, pregunta.escala_max)" :key="n"
                        class="px-2 py-3 text-center"
                      >
                        <label class="cursor-pointer inline-flex items-center justify-center">
                          <input
                            type="radio"
                            :name="`${pregunta.clave}_${sub.clave}`"
                            :value="String(n)"
                            v-model="respuestas[`${pregunta.clave}__${sub.clave}`]"
                            class="w-4 h-4 text-blue-600 accent-blue-600"
                          />
                        </label>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

            </div>
            <!-- fin loop preguntas -->

            <!-- Error de validación -->
            <div v-if="errorEnvio" class="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
              {{ errorEnvio }}
            </div>

            <!-- Botones de acción -->
            <div class="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                @click="submitEvaluacion"
                :disabled="enviando"
                class="inline-flex items-center justify-center px-6 py-2 rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                <svg v-if="enviando" class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                </svg>
                <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                {{ enviando ? 'Enviando...' : 'Enviar evaluación' }}
              </button>
              <button v-if="!preloaded" @click="cancelar"
                class="inline-flex items-center justify-center px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none">
                Cancelar
              </button>
            </div>
          </div>

          <!-- ÉXITO-->
          <div v-if="fase === 'exito'" class="mt-4 bg-green-100 border border-green-400 text-green-800 px-4 py-6 rounded-lg flex flex-col items-center gap-2 text-center">
            <svg class="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <p class="font-semibold text-lg">¡Evaluación enviada exitosamente!</p>
            <p class="text-sm text-green-700">Gracias por tu retroalimentación. Tu opinión contribuye a mejorar la calidad académica.</p>
            <button v-if="preloaded" @click="$router.back()"
              class="mt-2 px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700">
              Regresar
            </button>
          </div>

        </div>
      </main>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import { getPeriods, getSchedule } from '@/services/api';

// MODO PRUEBAS 
// FORCE_OPEN_WINDOW: true  -> omite la validación de ventana de tiempo activa.
//                              Útil cuando estás entre periodos o en vacaciones.
// FORCE_NOT_EVALUATED: true -> omite la verificación de "ya evaluado" (API mock).
// RECUERDA: ambas deben ser false en producción.
const DEBUG = {
  FORCE_OPEN_WINDOW:    true,   // <- true para ignorar ventana de tiempo
  FORCE_NOT_EVALUATED:  false,   // <- true para ignorar verificación de duplicado
};

//PLANTILLA DEFAULT (simulación de respuesta API)
//Este objeto representa lo que devolvería:
//Ejemplo: GET /api/evaluacion-docente/formulario?id_periodo=...&campus=...&codigo_materia=...
//
// La mayoría de las veces el backend devolverá este mismo formulario.
// TODO: Cuando se configure un formulario especial para un periodo/campus/materia, la misma API devolverá un objeto diferente con las preguntas que correspondan.
// El componente no necesita cambios: renderiza lo que reciba.
const PLANTILLA_DEFAULT = {
  plantilla_id: 1,
  config_id: null,
  nombre: 'Encuesta de Evaluación al Desempeño Docente',
  ventana_abierta: true, // el backend lo calcula; aquí siempre true en el mock
  preguntas: [
    {
      id: 1, orden: 1, clave: 'dominio_contenidos', tipo: 'radio', es_requerida: true, ayuda: null,
      texto: '¿Qué tan satisfecho te encuentras con el dominio de los contenidos y actualizaciones del docente?',
      opciones: [
        { valor: '5', etiqueta: 'Muy satisfecho' },
        { valor: '4', etiqueta: 'Satisfecho' },
        { valor: '3', etiqueta: 'Poco satisfecho' },
        { valor: '2', etiqueta: 'Insatisfecho' },
        { valor: '1', etiqueta: 'Muy insatisfecho' },
      ],
    },
    {
      id: 2, orden: 2, clave: 'estrategias_ensenanza', tipo: 'radio', es_requerida: true, ayuda: null,
      texto: '¿Qué tan satisfecho te encuentras con las estrategias de enseñanza que utiliza el docente en clase?',
      opciones: [
        { valor: '5', etiqueta: 'Muy satisfecho' },
        { valor: '4', etiqueta: 'Satisfecho' },
        { valor: '3', etiqueta: 'Poco satisfecho' },
        { valor: '2', etiqueta: 'Insatisfecho' },
        { valor: '1', etiqueta: 'Muy insatisfecho' },
      ],
    },
    {
      id: 3, orden: 3, clave: 'recursos_didacticos', tipo: 'checkbox', es_requerida: true,
      ayuda: 'Puedes seleccionar varios',
      texto: '¿Qué recursos didácticos utiliza el docente en clase?',
      opciones: [
        { valor: 'audiovisual',  etiqueta: 'Material audiovisual' },
        { valor: 'impreso',      etiqueta: 'Material impreso' },
        { valor: 'laboratorio',  etiqueta: 'Material de laboratorio' },
        { valor: 'digital',      etiqueta: 'Medios digitales' },
        { valor: 'otros',        etiqueta: 'Otros' },
      ],
    },
    {
      id: 4, orden: 4, clave: 'estrategias_evaluacion', tipo: 'radio', es_requerida: true, ayuda: null,
      texto: '¿Qué tan satisfecho te encuentras con las estrategias de evaluación que utiliza el docente?',
      opciones: [
        { valor: '5', etiqueta: 'Muy satisfecho' },
        { valor: '4', etiqueta: 'Satisfecho' },
        { valor: '3', etiqueta: 'Poco satisfecho' },
        { valor: '2', etiqueta: 'Insatisfecho' },
        { valor: '1', etiqueta: 'Muy insatisfecho' },
      ],
    },
    {
      id: 5, orden: 5, clave: 'habilidades_desarrolladas', tipo: 'checkbox', es_requerida: true,
      ayuda: 'Puedes seleccionar varios',
      texto: '¿Qué habilidades básicas desarrollaste con los aprendizajes obtenidos en tu asignatura?',
      opciones: [
        { valor: 'escritura',           etiqueta: 'Escritura' },
        { valor: 'lectura',             etiqueta: 'Lectura' },
        { valor: 'comunicacion_verbal', etiqueta: 'Comunicación verbal' },
        { valor: 'investigacion',       etiqueta: 'Investigación' },
      ],
    },
    {
      id: 6, orden: 6, clave: 'asesorias_academicas', tipo: 'radio', es_requerida: true, ayuda: null,
      texto: '¿Has recibido asesorías académicas por parte del docente?',
      opciones: [{ valor: 'si', etiqueta: 'Sí' }, { valor: 'no', etiqueta: 'No' }],
    },
    {
      id: 7, orden: 7, clave: 'relacion_vida_cotidiana', tipo: 'radio', es_requerida: true, ayuda: null,
      texto: '¿Existe relación entre lo visto en clase y su aplicación en la vida cotidiana?',
      opciones: [{ valor: 'si', etiqueta: 'Sí' }, { valor: 'no', etiqueta: 'No' }],
    },
    {
      id: 8, orden: 8, clave: 'relacion_vida_cotidiana_razon', tipo: 'textarea', es_requerida: true, ayuda: null,
      texto: 'Explica el por qué de tu respuesta anterior.',
    },
    {
      id: 9, orden: 9, clave: 'calidad_ensenanza', tipo: 'escala', es_requerida: true,
      escala_min: 1, escala_max: 5, ayuda: null,
      texto: '¿Cómo calificarías la calidad general de la enseñanza del docente?',
    },
    {
      id: 10, orden: 10, clave: 'satisfaccion_retroalimentacion', tipo: 'escala', es_requerida: true,
      escala_min: 1, escala_max: 5, ayuda: null,
      texto: '¿Cuál es tu nivel de satisfacción con la retroalimentación que recibes del docente sobre tu desempeño?',
    },
    {
      id: 11, orden: 11, clave: 'interaccion_docente', tipo: 'likert_grupo', es_requerida: true,
      escala_min: 1, escala_max: 5, ayuda: null,
      texto: 'Evalúa los siguientes aspectos de la interacción del docente contigo:',
      subpreguntas: [
        { id: 1, clave: 'fomenta_participacion', texto: 'Fomenta la participación en clase' },
        { id: 2, clave: 'resuelve_dudas',        texto: 'Resuelve dudas de manera clara y oportuna' },
        { id: 3, clave: 'respeto_imparcialidad', texto: 'Muestra respeto e imparcialidad hacia los estudiantes' },
      ],
    },
    {
      id: 12, orden: 12, clave: 'asistencia_puntualidad', tipo: 'escala', es_requerida: true,
      escala_min: 1, escala_max: 5, ayuda: null,
      texto: '¿Consideras que el docente mantiene un adecuado nivel de asistencia y puntualidad durante el curso?',
    },
  ],
};
//

export default {
  name: 'EvaluacionDocente',
  components: { SidebarMenu },

  data() {
    return {
      user: null,
      isMenuOpen: false,
      studentId: null,

      // Datos para selector manual
      periodos: [],
      scheduleData: [],
      scheduleKey: '',

      // Contexto de la evaluación (se llena desde query o selector)
      ctx: {
        id_periodo:    '',
        campus:        '',
        codigo_materia:'',
        nombre_materia:'',
        nombre_docente:'',
        fecha_inicio:  '',
        fecha_fin:     '',
      },

      // Formulario recibido de la API (o plantilla default)
      formulario: null,

      // Respuestas del alumno: { clave: valor } para radio/escala/textarea
      //                        { clave: [val, val] } para checkbox
      //                        { 'clave_grupo__sub_clave': valor } para likert
      respuestas: {},

      // Estado de la pantalla
      // 'selector' | 'formulario' | 'ya_evaluado' | 'ventana_cerrada' | 'exito'
      fase: 'selector',

      cargandoFormulario: false,
      enviando: false,
      errorCarga: '',
      errorEnvio: '',
    };
  },

  computed: {
    // ¿Llegamos con datos preseleccionados desde Horario/Boleta?
    preloaded() {
      return !!(this.$route?.query?.codigo_materia);
    },
    subjectsForPeriod() {
      if (!this.ctx.id_periodo || !this.scheduleData.length) return [];
      const seen = new Set();
      return this.scheduleData
        .filter(item => {
          const key = `${item.subject}||${item.teacher}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map(item => ({
          key: `${item.subject}||${item.teacher}||${item.code_subject || item.subject}||${item.campus || ''}||${item.date_start}||${item.date_end}`,
          subject: item.subject,
          teacher: item.teacher,
        }));
    },
  },

  async created() {
    await this.getUserFromBackend();

    if (this.preloaded) {
      // Modo preseleccionado: cargar contexto desde query params
      const q = this.$route.query;
      this.ctx = {
        id_periodo:     q.id_periodo      || q.periodo || '',
        campus:         q.campus          || '',
        codigo_materia: q.codigo_materia  || '',
        nombre_materia: q.nombre_materia  || '',
        nombre_docente: q.nombre_docente  || '',
        fecha_inicio:   q.fecha_inicio    || '',
        fecha_fin:      q.fecha_fin       || '',
      };
      await this.cargarFormulario();
    } else {
      // Modo manual: cargar periodos y horario disponible
      await Promise.all([this.loadPeriods(), this.loadSchedule()]);
    }
  },

  methods: {
    //Autenticación
    async getUserFromBackend() {
      try {
        const res = await axios.get('/me');
        this.user = res.data.user;
        if (this.user.email?.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];
        }
      } catch {
        this.$router.push('/login');
      }
    },

    // Carga de datos para selector manual
    async loadPeriods() {
      try {
        const res = await getPeriods({ idstudent: this.studentId });
        this.periodos = res.data;
      } catch (e) { console.error('Error cargando periodos:', e); }
    },
    async loadSchedule() {
      try {
        const res = await getSchedule({ idstudent: this.studentId });
        this.scheduleData = res.data;
      } catch (e) { console.error('Error cargando horario:', e); }
    },

    onPeriodChange() { this.scheduleKey = ''; },
    onScheduleKeyChange() {
      if (!this.scheduleKey) return;
      const [subject, teacher, code, campus, fecha_inicio, fecha_fin] = this.scheduleKey.split('||');
      this.ctx.nombre_materia  = subject;
      this.ctx.nombre_docente  = teacher;
      this.ctx.codigo_materia  = code;
      this.ctx.campus          = campus;
      this.ctx.fecha_inicio    = fecha_inicio;
      this.ctx.fecha_fin       = fecha_fin;
    },

    async iniciarEvaluacion() {
      if (!this.scheduleKey) return;
      await this.cargarFormulario();
    },

    // Carga del formulario desde API (con fallback a plantilla default) ──
    async cargarFormulario() {
      this.cargandoFormulario = true;
      this.errorCarga = '';
      this.fase = 'selector';

      try {
        // PASO 1: Verificar si ya evaluó
        const yaEvaluo = await this.verificarEvaluacion();
        if (yaEvaluo) { this.fase = 'ya_evaluado'; return; }

        // PASO 2: Obtener formulario dinámico de la API
        const formularioResuelto = await this.fetchFormulario();

        // PASO 3: Verificar ventana de tiempo
        if (!formularioResuelto.ventana_abierta && !DEBUG.FORCE_OPEN_WINDOW) {
          this.fase = 'ventana_cerrada';
          return;
        }

        this.formulario = formularioResuelto;
        this.respuestas = this.inicializarRespuestas(formularioResuelto.preguntas);
        this.fase = 'formulario';
      } catch (e) {
        console.error('Error cargando formulario:', e);
        this.errorCarga = 'No se pudo cargar el formulario. Intenta de nuevo.';
      } finally {
        this.cargandoFormulario = false;
      }
    },

    // Verifica si el alumno ya realizó esta evaluación
    async verificarEvaluacion() {
      if (DEBUG.FORCE_NOT_EVALUATED) return false;

      // TODO: cuando se tenga el endpoint real, descomentar y eliminar el return false:
      // const res = await axios.get('/api/evaluacion-docente/verificar', {
      //   params: {
      //     matricula:      this.studentId,
      //     id_periodo:     this.ctx.id_periodo,
      //     codigo_materia: this.ctx.codigo_materia,
      //   }
      // });
      // return res.data.evaluada === true;

      return false; // <- simulación: aún no hay API
    },

    // Fetch del formulario dinámico
    async fetchFormulario() {
      // TODO: cuando se tenga el endpoint listo, descomentar este bloque
      // y eliminar el return PLANTILLA_DEFAULT de abajo:
      //
      // try {
      //   const res = await axios.get('/api/evaluacion-docente/formulario', {
      //     params: {
      //       id_periodo:     this.ctx.id_periodo,
      //       campus:         this.ctx.campus,
      //       codigo_materia: this.ctx.codigo_materia,
      //     }
      //   });
      //   return res.data; // El objeto ya viene con { ventana_abierta, preguntas, ... }
      // } catch (e) {
      //   console.warn('API de formulario no disponible, usando plantilla default:', e);
      //   // Fallback: si la API falla, usar plantilla default local
      //   return { ...PLANTILLA_DEFAULT, ventana_abierta: true };
      // }

      // Simulación mientras no hay API 
      // Se simula la "ventana de tiempo" usando las fechas que vienen del
      // horario/boleta (ctx.fecha_inicio / ctx.fecha_fin).
      // Cuando llegue el endpoint real, esto se reemplaza por la respuesta del servidor.
      let ventana_abierta = false;
      if (DEBUG.FORCE_OPEN_WINDOW) {
        ventana_abierta = true;
      } else if (this.ctx.fecha_inicio && this.ctx.fecha_fin) {
        const now   = new Date();
        const start = new Date(this.ctx.fecha_inicio);
        const end   = new Date(this.ctx.fecha_fin);
        ventana_abierta = now >= start && now <= end;
      }

      return { ...PLANTILLA_DEFAULT, ventana_abierta };
      
    },

    // Inicializa el objeto respuestas según los tipos de pregunta ───
    inicializarRespuestas(preguntas) {
      const r = {};
      preguntas.forEach(p => {
        if (p.tipo === 'checkbox') {
          r[p.clave] = [];
        } else if (p.tipo === 'likert_grupo') {
          (p.subpreguntas || []).forEach(sub => {
            r[`${p.clave}__${sub.clave}`] = null;
          });
        } else {
          r[p.clave] = null;
        }
      });
      return r;
    },

    // Helpers de UI─
    escalaRango(min, max) {
      const arr = [];
      for (let i = (min ?? 1); i <= (max ?? 5); i++) arr.push(i);
      return arr;
    },
    toggleCheckbox(clave, valor) {
      if (!this.respuestas[clave]) this.respuestas[clave] = [];
      const idx = this.respuestas[clave].indexOf(valor);
      if (idx === -1) this.respuestas[clave].push(valor);
      else this.respuestas[clave].splice(idx, 1);
    },

    // Validación dinámica
    validar() {
      if (!this.formulario) return 'Formulario no cargado.';

      for (const [idx, p] of this.formulario.preguntas.entries()) {
        if (!p.es_requerida) continue;
        const num = idx + 1;

        if (p.tipo === 'checkbox') {
          if (!(this.respuestas[p.clave]?.length)) return `Pregunta ${num}: selecciona al menos una opción.`;
        } else if (p.tipo === 'likert_grupo') {
          for (const sub of (p.subpreguntas || [])) {
            if (!this.respuestas[`${p.clave}__${sub.clave}`]) {
              return `Pregunta ${num}: evalúa el aspecto "${sub.texto}".`;
            }
          }
        } else {
          const val = this.respuestas[p.clave];
          if (val === null || val === undefined || String(val).trim() === '') {
            return `Pregunta ${num}: campo obligatorio sin respuesta.`;
          }
        }
      }
      return null;
    },

    // Construcción del payload
    buildPayload() {
      const respuestasArray = this.formulario.preguntas.map(p => {
        const base = {
          pregunta_id: p.id,
          clave: p.clave,
          tipo: p.tipo,
        };

        if (p.tipo === 'checkbox') {
          return { ...base, valores: this.respuestas[p.clave] || [] };
        }
        if (p.tipo === 'likert_grupo') {
          return {
            ...base,
            subrespuestas: (p.subpreguntas || []).map(sub => ({
              subpregunta_id: sub.id,
              clave: sub.clave,
              valor: this.respuestas[`${p.clave}__${sub.clave}`],
            })),
          };
        }
        return { ...base, valor: this.respuestas[p.clave] };
      });

      return {
        matricula:        this.studentId,
        id_periodo:       this.ctx.id_periodo,
        campus:           this.ctx.campus,
        codigo_materia:   this.ctx.codigo_materia,
        nombre_materia:   this.ctx.nombre_materia,
        nombre_docente:   this.ctx.nombre_docente,
        plantilla_id:     this.formulario.plantilla_id,
        config_id:        this.formulario.config_id,
        fecha_evaluacion: new Date().toISOString(),
        respuestas:       respuestasArray,
      };
    },

    // Envío
    async submitEvaluacion() {
      this.errorEnvio = '';
      const err = this.validar();
      if (err) { this.errorEnvio = err; return; }

      this.enviando = true;
      const payload = this.buildPayload();
      console.log('PAYLOAD EVALUACIÓN DOCENTE:', JSON.stringify(payload, null, 2));

      try {
        // TODO: descomentar cuando se tenga el endpoint:
        // await axios.post('/api/evaluacion-docente', payload);

        await new Promise(r => setTimeout(r, 700)); // simulación
        this.fase = 'exito';
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        console.error('Error enviando evaluación:', e);
        this.errorEnvio = 'Ocurrió un error al enviar. Por favor, intenta de nuevo.';
      } finally {
        this.enviando = false;
      }
    },

    cancelar() {
      this.fase = 'selector';
      this.scheduleKey = '';
      this.ctx = { id_periodo:'', campus:'', codigo_materia:'', nombre_materia:'', nombre_docente:'', fecha_inicio:'', fecha_fin:'' };
      this.formulario = null;
      this.respuestas = {};
    },

    toggleMenu() { this.isMenuOpen = !this.isMenuOpen; },
    closeMenu()  { this.isMenuOpen = false; },
    async logout() {
      try {
        await axios.get('/sanctum/csrf-cookie');
        await axios.post('/logout', {}, { withCredentials: true });
        document.cookie.split(';').forEach(c => {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
        });
        this.$router.push('/login');
      } catch (e) { console.error(e); }
    },
  },
};
</script>